import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApplication } from '../hooks/useApplication';
import { getCompanyFromUrl, isCompanySubdomain } from '../utils/company';
import JobSearchBar from './JobSearchBar';

const Header: React.FC = () => {
  const location = useLocation();
  const { openApplicationModal, currentJobContext } = useApplication();
  
  // Check if we're on a job page
  const isJobPage = location.pathname.startsWith('/job/');
  
  // Determine current company (prefer subdomain)
  const getCompany = (): string => getCompanyFromUrl();
  
  // Generate company-specific route
  const getCompanyRoute = (page: string): string => {
    const company = getCompany();
    // On company subdomains, keep clean paths without prefix
    if (isCompanySubdomain()) return `/${page}`;
    // On path-based variant, include company in path
    return `/${company}/${page}`;
  };

  // Get logo configuration based on current company
  const getLogoConfig = () => {
    const company = getCompany();

    switch (company) {
      case 'talentschmiede':
        return {
          desktop: '/talentschmiedeLogo.png',
          mobile: '/talentschmiedeLogo.png',
          alt: 'Talentschmiede'
        };
      case 'digitaldavid':
        return {
          desktop: '/digitaldavidlogo.png',
          mobile: '/digitaldavidlogo.png',
          alt: 'Digital David'
        };
      default: // dwg
        return {
          desktop: '/logo-long.png',
          mobile: '/logo.png',
          alt: 'Digital Workforce Group'
        };
    }
  };
  
  const logoConfig = getLogoConfig();

  return (
    <nav className="navbar">
      <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', justifyContent: 'space-between' }}>
        <Link to={isCompanySubdomain() ? '/' : `/${getCompany()}`} className="nav-brand">
          <img
            src={logoConfig.desktop}
            alt={logoConfig.alt}
            className="logo-desktop"
            style={{
              height: '50px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
          <img
            src={logoConfig.mobile}
            alt={logoConfig.alt}
            className="logo-mobile"
            style={{
              height: '40px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </Link>

        {/* Spacer to push nav-menu to the right */}
        <div style={{ flex: 1 }}></div>

        <div className="nav-menu">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Search Bar */}
            <JobSearchBar />

            {/* FAQ Link - Always visible */}
            <Link
              to={getCompanyRoute('faq')} 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                color: '#64748b',
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                padding: '0.6rem 1.25rem',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.color = '#1e293b';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fafc';
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.color = '#64748b';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              FAQ
            </Link>
            
            {/* Bewerben Button - Always visible */}
            <button
              onClick={() => openApplicationModal(isJobPage ? (currentJobContext || undefined) : undefined)}
              className="bewerben-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1.5rem',
                backgroundColor: '#1e40af',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1d4ed8';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1e40af';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="8.5" cy="7" r="4"></circle>
                <line x1="20" y1="8" x2="20" y2="14"></line>
                <line x1="23" y1="11" x2="17" y2="11"></line>
              </svg>
              Bewerben
            </button>

            {/* Back Button - Only visible on job pages */}
            {isJobPage && (
              <button
                onClick={() => {
                  console.log('Back button clicked, navigating to home');
                  window.location.href = '/';
                }}
                className="back-button"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  color: '#475569',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.color = '#3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = '#475569';
                }}
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
                Zurück zur Übersicht
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
